<template>
  <div class="product-quantity" v-bind="inert">
    <button class="product-quantity__button product-quantity__button--minus" @click="updateQuantity(displayQuantity - 1)"></button>

    <input
      ref="quantity"
      v-model.number="displayQuantity"
      :type="inputType"
      class="product-quantity__input"
      @keyup.enter="onBlur"
      @blur="onBlur"
      @input="emitQuantity(displayQuantity)"
      @focus="selectInputValue()">

    <button class="product-quantity__button product-quantity__button--plus" @click="updateQuantity(displayQuantity + 1)"></button>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import _ from 'lodash'
import gtm from '@/core/enhanced-ecommerce-gtm'
import { listTypes } from '@/core/analytics-variables'

export default {
  props: {
    cartRow: {
      type: Object,
      required: true
    },
    cartName: {
      type: String,
      default: ''
    },
    stopQuantityEvents: {
      type: Boolean,
      default: false
    },
    value: {
      type: Number,
      required: false,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      update: _.debounce(this.doUpdate, 800),
      desiredQuantity: 0,
      currentQuantity: null
    }
  },
  computed: {
    ...mapGetters(['enableGtm']),
    inputType() {
      return this.mq('medium') ? 'input' : 'number'
    },
    displayQuantity: {
      get: function() {
        return this.desiredQuantity > 0 && this.desiredQuantity !== this.cartRow.quantity ? this.desiredQuantity : this.cartRow.quantity
      },
      set: function (newValue) {
        this.desiredQuantity = newValue
      }
    },
    inert() {
      return this.disabled ? { inert: true } : {}
    }
  },
  methods: {
    ...mapActions('commerce', ['changeQuantity']),
    doUpdate() {
      if (this.stopQuantityEvents || (this.cartRow.quantity === this.displayQuantity)) {
        return
      }

      this.currentQuantity = this.cartRow.quantity

      this.changeQuantity([{ cartName: this.cartName, lineItemId: this.cartRow.id, quantity: this.desiredQuantity }]).then(() => {
        if (this.enableGtm) {
          this.gtmAddToCart(this.currentQuantity)
        }

        this.desiredQuantity = 0
      })
    },
    onBlur(evt) {
      this.doUpdate()
    },
    updateQuantity(desiredQuantity) {
      this.displayQuantity = desiredQuantity
      this.update()
      this.emitQuantity(desiredQuantity)
    },
    emitQuantity(quantity) {
      if (this.stopQuantityEvents) {
        this.$emit('input', quantity)
      }
    },
    gtmAddToCart(currentQuantity) {
      const gtmObject = gtm.createGtmItem(
        this.cartRow.itemId,
        this.cartRow.displayName,
        this.cartRow.item.purchasedToVehicle,
        this.cartRow.item.purchasedOnCategory,
        this.formatPriceWithoutCurrency(this.cartRow.priceExVat),
        0, // quantity
        listTypes.CART) // list type

      if (this.desiredQuantity > currentQuantity) {
        gtmObject.quantity = this.desiredQuantity - currentQuantity

        gtm.addToCart(gtmObject)
      } else {
        gtmObject.quantity = currentQuantity - this.desiredQuantity
        gtm.removeFromCart(gtmObject)
      }
    },
    selectInputValue() {
      this.$refs.quantity.setSelectionRange(0, this.$refs.quantity.value.length)
    }
  }
}
</script>

<style lang="scss">
.product-quantity {
  display: flex;
  align-items: center;
  width: auto;
  padding: measure(eighth) measure(quarter);
  background-color: color(background-blue-5);
}

.product-quantity[inert] {
  opacity: 0.5;
}

.product-quantity--border {
  display: none;

  @include media(small) {
    display: flex;
    height: 3.125rem;
    border: 2px solid color(border-blue-4);
  }
}

.product-quantity__button {
  @include reset-button;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 0.9375rem;
  height: 0.9375rem;
  background-repeat: no-repeat;
  background-position: center;

  &:hover {
    cursor: pointer;
  }
}

.product-quantity--border .product-quantity__input {
  height: 100%;
}

.product-quantity__button--minus {
  @include background-svg(circleMinus);
}

.product-quantity__button--plus {
  @include background-svg(circlePlus);
}

.product-quantity__input {
  @include caption-semi-bold;
  width: 1.875rem;
  height: 1.875rem;
  margin: 0 measure(quarter);
  text-align: center;
  background-color: color(background-white);
  border: none;
}
</style>
